/**--external-- */
import React, { useRef } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { MailColored, ProfileOutlined } from "@toddle-edu/ds-icons";
import { Button } from "@toddle-edu/ds-web";

/**--internal-- */
import { GOOGLE_CLIENT_ID } from "Constants";
import GoogleLoginBtn from "UIComponents/GoogleLoginBtn";
import MicrosoftLoginBtn from "UIComponents/MicrosoftLoginBtn";
import CustomLoginButton from "UIComponents/CustomLoginButton";
import GridLayoutWrapper from "UIComponents/GridLayoutWrapper";
import {FormTextInput} from "UIComponents/InputTextField";

/**--relative-- */
import classes from "./VisitorLogin.scss";
import { innerConStyle } from "./VisitorLoginStyles";
import { getIsChina } from "config";

const VisitorLogin = props => {
  const {
    renderBackButtonComponent,
    updateCurrentScreen,
    onGoogleSignInCallBack,
    onGoogelFailureCallBack,
    onMicroSoftSignInCallBack,
    onMicroSoftFailureCallBack,
    isNewLoginFlowEnabled,
    data: { email = "" } = {},
    updateInputField,
    checkEmailVerification,
    generateEmailOTPForSignIn,
    userType,
    role,
    resetScreen,
    customStyle: { headerStyleV2 } = {},
    isOTPLoginEnabled,
    isOrgWorkspaceLoginPage,
    isPasswordLoginEnabled,
    renderTradeMarkText,
    organizationName,
    enableLoginFlowV2,
  } = props;

  const inputRef = useRef();
  const isChinaRegion = getIsChina();

  const onBackClick = () => {
    if (isNewLoginFlowEnabled) {
      resetScreen("visitorLogin");
    }
    updateCurrentScreen({
      screen: "loginHome",
      paramsToBeRemoved: ["role", "usertype"],
    });
  };

  const onLoginClick = () => {
    updateCurrentScreen({
      type: enableLoginFlowV2 ? "loginFormV3" : "loginForm",
      userType: "staff",
      role: "visitor",
    });
  };

  const onEmailSubmit = async () => {
    const errorCount = inputRef.current.isValid();
    if (errorCount > 0) {
      return;
    }

    if (isOTPLoginEnabled) {
      const { isSuccess } = await generateEmailOTPForSignIn({
        userType,
        role,
      });
      if (isSuccess) {
        updateCurrentScreen({ type: "loginFormV2" });
      }
    } else {
      updateInputField({ email }, "loginForm");
      updateCurrentScreen({ type: "loginForm" });
    }
  };

  const isEmailLoginFlowEnabled =
    (isOTPLoginEnabled || isPasswordLoginEnabled) && isNewLoginFlowEnabled;

  const children = [
    <MicrosoftLoginBtn
      onSuccess={onMicroSoftSignInCallBack}
      onFailure={onMicroSoftFailureCallBack}
      className={classes.microsoftButtonContainer}
      innerConStyle={innerConStyle}
      key="MICROSOFT_LOGIN_BTN"
    />,
  ];

  if (!isChinaRegion) {
    children.unshift(
      <GoogleLoginBtn
        clientId={GOOGLE_CLIENT_ID}
        onSuccess={onGoogleSignInCallBack}
        onFailure={onGoogelFailureCallBack}
        prompt="select_account"
        innerConStyle={innerConStyle}
        key="GOOGLE_LOGIN_BTN"
      />
    );
  }

  if (enableLoginFlowV2 && isEmailLoginFlowEnabled) {
    children.unshift(
      <CustomLoginButton
        data-test-id={"button-login-email"}
        icon={<MailColored size="x-small" />}
        buttonText={"Email"}
        onClick={onLoginClick}
        innerConStyle={innerConStyle}
        key="EMAIL_LOGIN_BTN"
      />
    );
  }

  const buttonsContainerStyle = classNames({
    [classes.buttonsContainer]: true,
    [classes.buttonsContainerV2]: isNewLoginFlowEnabled,
  });

  const containerStyle = {
    paddingTop: isOrgWorkspaceLoginPage ? "13%" : "19.5%",
  };

  return (
    <div className={classes.container} style={containerStyle}>
      <div>
        {renderBackButtonComponent({
          onClick: onBackClick,
          buttonText: isOrgWorkspaceLoginPage
            ? organizationName
            : "Toddle home",
        })}
        <div style={headerStyleV2}>{`Visitor account`}</div>
        <div className={buttonsContainerStyle}>
          {isNewLoginFlowEnabled && (
            <div className={classes.helperText}>{"Sign in using"}</div>
          )}

          <GridLayoutWrapper
            noOfColumns={2}
            itemsPerRow={2}
            stretchItemsInLastRow={false}
          >
            {children}
          </GridLayoutWrapper>
        </div>
        {isEmailLoginFlowEnabled && !enableLoginFlowV2 ? (
          <>
            {isNewLoginFlowEnabled ? (
              <div className={classes.orContainer}>
                <div className={classes.orLineContainer} />
                <div className={classes.orText}>or</div>
                <div className={classes.orLineContainer} />
              </div>
            ) : null}

            <div className={classes.bottomContainer}>
              <div className={classes.emailContainer}>
                <div className={classes.emailText}>
                  {isOTPLoginEnabled
                    ? "Sign in with email OTP"
                    : "Sign in with email"}
                </div>
                <div className={classes.inputRowContainer}>
                  <div className={classes.inputField}>
                    <FormTextInput
                      placeholder="Enter your email id"
                      ref={inputRef}
                      value={email}
                      name={"email"}
                      customValidation={checkEmailVerification}
                      size={"large"}
                      updateInputField={updateInputField}
                      onEnter={onEmailSubmit}
                      leadingIcon={
                        <ProfileOutlined size="xx-small" variant="subtle" />
                      }
                      error={"Enter valid email"}
                      autoComplete={"off"}
                      autoFocus={true}
                    />
                  </div>
                  <Button
                    variant={"destructive"}
                    disabled={email.length === 0}
                    onClick={onEmailSubmit}
                    size={"large"}
                    className={classes.submitButton}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </div>
          </>
        ) : null}
      </div>
      <div>
        {renderTradeMarkText({
          align: "left",
        })}
      </div>
    </div>
  );
};

export default VisitorLogin;

VisitorLogin.displayName = "VisitorLogin";

VisitorLogin.propTypes = {
  renderBackButtonComponent: PropTypes.func.isRequired,
  updateCurrentScreen: PropTypes.func.isRequired,
  onGoogleSignInCallBack: PropTypes.func.isRequired,
  onMicroSoftSignInCallBack: PropTypes.func.isRequired,
  onMicroSoftFailureCallBack: PropTypes.func.isRequired,
};
